import { Col } from '@amzn/stencil-react-components/layout';
import { Text } from '@amzn/stencil-react-components/text';
import { ButtonCard } from '@amzn/stencil-react-components/card';
import { token } from '@amzn/stencil-design-tokens/js/web/utils';

/**
 * Ring on the open tile, so the panel below is visibly attached to one of them.
 *
 * Goes through `css` rather than the `border` prop: an interactive Card spreads its own
 * `border` after the props it was given, so the prop is silently discarded.
 *
 * Hoisted so emotion sees one stable object rather than a new one each render.
 */
const SELECTED_RING = {
  border: `${token('dimensions.border.width.200')} solid ${token('color.status.info-border')}`,
} as const;

/**
 * Surface of a tile, open or not.
 *
 * A Card's `backgroundColor` is not the same prop as a `Col`'s: it takes a resolved CSS
 * value and runs it through an adapter that only knows the legacy palette names, so a
 * dotted token path reaches the stylesheet verbatim and the declaration is dropped.
 * `token()` resolves it here instead.
 */
const SURFACE = {
  selected: token('color.surface.bg-muted'),
  resting: token('color.surface.bg-default'),
} as const;

/** The count leads, so it is the heaviest weight; its label sits one step below. */
const COUNT_WEIGHT = 'bold';
const LABEL_WEIGHT = 'medium';

/**
 * Tone of the count.
 *
 * `critical` and `warning` match the prototype's red for failures and amber for actions;
 * `positive` and `muted` carry the pass count and the skipped count. A tile with no tone
 * takes the default text colour, which suits a figure that is neither good nor bad news.
 */
export type TileTone = 'critical' | 'warning' | 'positive' | 'muted';

const TONE_COLORS = new Map<TileTone, string>([
  ['critical', 'color.status.critical-icon'],
  ['warning', 'color.status.warning-icon'],
  ['positive', 'color.status.success-icon'],
  ['muted', 'color.text.secondary'],
]);

/**
 * What selecting a tile does, which decides how its state is announced.
 *
 * `panel` reveals a region below, which is what `aria-expanded` describes. `filter` leaves
 * the region in place and changes what it holds, which it does not — that is a button with
 * an on state, so it is announced as pressed instead.
 */
export type TileSelects = 'panel' | 'filter';

interface InsightTileProps {
  count: number;
  label: string;
  /** A second line under the label. Absent on a tile whose label says enough. */
  subLabel?: string;
  /** Absent for a count that is neither good nor bad news. */
  tone?: TileTone;
  isSelected: boolean;
  /** Defaults to `panel`. */
  selects?: TileSelects;
  onClick: () => void;
}

/**
 * A clickable count that opens the panel listing what it counts.
 *
 * `ButtonCard` carries card chrome on a real `button` element, so the keyboard and
 * screen reader get the affordance for free — the prototype's onclick div has neither.
 * A plain `Button` cannot hold this content: it is a single-line control with a pill
 * radius, so three stacked lines render as an ellipse, and its primary variant repaints
 * the count and sub-label in button-foreground, losing the tone.
 *
 * Takes an equal share of its row rather than sizing to its content, so the tiles span
 * the section and stay the same width whether a count is one digit or three.
 */
export function InsightTile({
  count,
  label,
  subLabel,
  tone,
  isSelected,
  selects = 'panel',
  onClick,
}: InsightTileProps): JSX.Element {
  const toneColor = tone ? TONE_COLORS.get(tone) : undefined;

  return (
    <ButtonCard
      type="button"
      onClick={onClick}
      {...(selects === 'filter'
        ? { 'aria-pressed': isSelected }
        : { 'aria-expanded': isSelected })}
      backgroundColor={isSelected ? SURFACE.selected : SURFACE.resting}
      padding="dimensions.spacing.300"
      flex={1}
      {...(isSelected ? { css: SELECTED_RING } : {})}
    >
      {/* Full width so the count centres on the tile, not on its own text box: the card
          is a flex row, and without this the column shrinks to its content. */}
      <Col alignItems="center" gridGap="dimensions.spacing.50" width="100%">
        <Text
          fontSize="T500"
          fontWeight={COUNT_WEIGHT}
          {...(toneColor ? { color: toneColor } : {})}
        >
          {count}
        </Text>
        <Text fontSize="T100" fontWeight={LABEL_WEIGHT}>
          {label}
        </Text>
        {subLabel ? (
          // T50, not T75: Stencil has no T75 step, and passing one emits the literal
          // `font-size: T75`, which a browser drops — leaving this line the same size as
          // the label above it.
          <Text fontSize="T50" color="color.text.secondary">
            {subLabel}
          </Text>
        ) : null}
      </Col>
    </ButtonCard>
  );
}
